'use client'

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { snapshotAndUpdateQueryOptions } from '@/service/snapshot-query-options'
import { fetchTokenEmission, fetchTokenUnlocks } from '@/service/tokenomist.api'
import type { TokenUnlockHistoryParams } from '@/service/tokenomist.types'
import type {
  GetTokenEmissionRequest,
  TokenEmissionSeries,
  TokenUnlockEventList,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

export { useTokenUnlockStream } from '@/service/ws/use-token-unlock-stream'

const TOKEN_UNLOCKS_DEFAULT_LIMIT = 100

export const tokenomistKeys = {
  all: ['tokenomist'] as const,
  unlocks: (params: Omit<TokenUnlockHistoryParams, 'offset'>) =>
    [...tokenomistKeys.all, 'unlocks', params] as const,
  emission: (params: GetTokenEmissionRequest) =>
    [...tokenomistKeys.all, 'emission', params.token, params.from, params.to] as const,
}

/**
 * Pure, unit-tested independently of the query hook: three post-`LIMIT` filters
 * run server-side (superseded rows, `min_market_cap`, `min_value_to_market_cap`),
 * so a page can come back short of `limit` without being the end of the feed
 * (`refill_cap_hit`, rare but real). Only an empty page means "stop" — the
 * opposite of `useCoinMarketCalEventsInfinite`'s `< limit` rule.
 */
export function nextTokenUnlocksPageParam(
  lastPage: { events: unknown[] },
  allPages: unknown[],
  limit: number
): number | undefined {
  return lastPage.events.length === 0 ? undefined : allPages.length * limit
}

/**
 * `params` deliberately omits `offset` — `useInfiniteQuery` owns pagination
 * end-to-end via `pageParam`/`initialPageParam: 0`, so a caller-supplied `offset`
 * would just be silently overwritten.
 */
export function useTokenUnlocksInfinite(
  params: Omit<TokenUnlockHistoryParams, 'offset'>,
  enabled = true
) {
  const limit = params.limit ?? TOKEN_UNLOCKS_DEFAULT_LIMIT
  return useInfiniteQuery<TokenUnlockEventList>({
    queryKey: tokenomistKeys.unlocks(params),
    queryFn: ({ pageParam = 0 }) =>
      fetchTokenUnlocks({ ...params, limit, offset: pageParam as number }),
    getNextPageParam: (lastPage, allPages) => nextTokenUnlocksPageParam(lastPage, allPages, limit),
    initialPageParam: 0,
    enabled,
    ...snapshotAndUpdateQueryOptions,
  })
}

export function useTokenEmission(params: GetTokenEmissionRequest, enabled = true) {
  return useQuery<TokenEmissionSeries, ErrorResponse>({
    queryKey: tokenomistKeys.emission(params),
    queryFn: ({ signal }) => fetchTokenEmission(params, signal),
    enabled: enabled && params.token.trim().length > 0,
    // An ambiguous-symbol 400 must never be silently retried — it's a request
    // shape problem (needs a slug instead of a symbol), not a transient failure.
    retry: (count, error) => error.status >= 500 && count < 2,
    staleTime: 60 * 60 * 1000,
  })
}
